import { ReactNode, useEffect, useRef, useState } from 'react';

import useIntersectionObserver from '@virginexperiencedays/hooks/src/useIntersectionObserver';
import { cn } from '@virginexperiencedays/tailwind';

import { Divider } from '../../../layout/Divider';
import { LinkWrap } from '../../../navigation/LinkWrap';

import { PricingModule } from '../PricingModule';
import { StockCountdown } from '../StockCountDown';

type Price = {
  rrp: number;
  currency: string;
  displayPrice: number;
  percentOff: number;
  fixedAmountOff: number;
};

export type ProductPriceDetailsProps = {
  displayPriceDetails?: boolean;
  isCollectionChoice?: boolean;
  collectionLink?: string;
  price: Price;
  stock: number;
  unitsAvailableIsPublic?: boolean;
  isActive?: boolean;
  klarnaComponent?: ReactNode;
  stripeComponent?: ReactNode;
  addToBasketComponent?: ReactNode;
  uspsComponent?: ReactNode;
  onClickBackToCollection?: () => void;
};

export const ProductPriceDetails = ({
  displayPriceDetails = true,
  isCollectionChoice,
  collectionLink,
  price,
  stock,
  unitsAvailableIsPublic,
  isActive = false,
  klarnaComponent,
  stripeComponent,
  addToBasketComponent,
  uspsComponent,
  onClickBackToCollection,
}: ProductPriceDetailsProps) => {
  const [stuck, setStuck] = useState(false);
  const stickyRef = useRef(null);

  const stickyRefEntry = useIntersectionObserver(stickyRef, {
    threshold: [1],
    disabled: !isActive,
  });

  useEffect(() => {
    setStuck(stickyRefEntry?.intersectionRatio < 1);
  }, [stickyRefEntry]);

  if (!displayPriceDetails) return null;

  const showKlarna = price.displayPrice > 35 && !!klarnaComponent;

  return (
    <>
      {isCollectionChoice ? (
        <>
          {collectionLink ? (
            <BackToCollectionLink href={collectionLink} onClick={onClickBackToCollection} />
          ) : null}
          {!isActive ? <StyledDivider /> : null}
        </>
      ) : (
        <>
          <div
            ref={stickyRef}
            className={cn(
              '-mx-4 mb-0 mt-4 flex flex-wrap items-center gap-10 gap-y-2 px-4 py-2',
              // Basket Error
              '[&_p]:mt-0 [&_p]:w-full',
              // Sticky
              isActive && 'sticky -top-px z-[1]',
              // Sticky and Stuck
              isActive && stuck && 'bg-background-page shadow-lg'
            )}
          >
            <PricingModule pricing={price} />
            {isActive && (addToBasketComponent ?? null)}
          </div>

          {stock > 0 && unitsAvailableIsPublic ? <StockCountdown stock={stock} /> : null}

          {isActive && (
            <>
              {stripeComponent ?? null}
              {showKlarna && klarnaComponent}
              {uspsComponent && (
                <div className="hidden [@media(min-width:388px)]:block">
                  {showKlarna && <StyledDivider />}
                  {uspsComponent}
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

type BackToCollectionProps = { href: string; onClick?: () => void };

const StyledDivider = () => <Divider className="m-0 first-of-type:mt-6" />;

const BackToCollectionLink = ({ href, onClick }: BackToCollectionProps) => {
  return (
    <div className="my-6 inline-block w-full" data-testid="back-to-collection-link">
      <LinkWrap
        className={cn(
          'bg-background-primary-faded text-neutral hover:bg-background-primary-faded-hover',
          'flex h-12 items-center justify-center',
          'text-base font-semibold capitalize no-underline',
          'select-none rounded border border-2 border-transparent p-4 md:p-4',
          'focus:border-tones-info-500 focus:outline-0'
        )}
        href={href}
        onClick={onClick}
      >
        Back To Collection
      </LinkWrap>
    </div>
  );
};
